import React from "react";
import { connect } from "react-redux";

import Dialog from "../core/components/dialog/Dialog";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import Grid from "../core/components/grid/Grid";
import GridColumn from "../core/components/grid/GridColumn";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import { AppDispatch, IAppState } from "../core/state/Store";
import { Side, Size } from "../core/types/system/Layout";
import Text from "../core/components/text/Text";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import MessageLog from "../core/types/system/MessageLog";
import Tabs from "../core/components/tabs/Tabs";
import TabContainer from "../core/components/tabs/TabContainer";
import PageSpecification from "../core/types/system/PageSpecification";
import {
    getEmailLogPage,
    openEmailLog,
    setEmailLogPageAndSize,
} from "../core/state/system/EmailLog";
import Panel from "../core/components/panels/Panel";
import ContentPortal from "../core/components/content/ContentPortal";
import MessageLogRecipient from "../core/types/system/MessageLogRecipient";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import DialogCancel from "../core/components/dialog/DialogCancel";
import {
    getMessageLogPage,
    openMessageLog,
    setMessageLogPageAndSize,
} from "../core/state/system/MessageLog";

import "./MessageLogs.scss";

interface RouteMessageLogsProps {
    emails: MessageLog[];
    emailsPageSpec: PageSpecification;
    email?: MessageLog;
    emailRecipients?: MessageLogRecipient[];
    emailOpenStatus: string;

    messages: MessageLog[];
    messagesPageSpec: PageSpecification;
    message?: MessageLog;
    messageRecipients?: MessageLogRecipient[];
    messageOpenStatus: string;

    setPageLoading: (loading: boolean) => void;
    getEmailLogPage: () => Promise<void>;
    setEmailLogPageAndSize: (spec: PageSpecification) => void;
    openEmailLog: (email: MessageLog) => void;
    getMessageLogPage: () => Promise<void>;
    setMessageLogPageAndSize: (spec: PageSpecification) => void;
    openMessageLog: (message: MessageLog) => void;
}

interface RouteMessageLogsState {
    viewOpen: boolean;
    viewTitle: string;
    viewing?: MessageLog;
    tabOpen?: string;
}

class RouteMessageLogs extends React.Component<
    RouteMessageLogsProps,
    RouteMessageLogsState
> {
    state: RouteMessageLogsState = {
        viewOpen: false,
        viewTitle: "",
        tabOpen: "Email Logs",
    };

    async tabChanged(newTab: string) {
        try {
            this.props.setPageLoading(true);
            switch (newTab) {
                case "Email Logs":
                    await this.props.getEmailLogPage();
                    break;
                case "Message Logs":
                    await this.props.getMessageLogPage();
                    break;
            }
        } finally {
            this.setState({ viewing: undefined, tabOpen: newTab });
            this.props.setPageLoading(false);
        }
    }

    view() {
        this.props.openEmailLog(this.state.viewing!);
        this.setState({
            viewOpen: true,
            viewTitle:
                this.state.tabOpen === "Email Logs"
                    ? "Email Details"
                    : "Message Details",
        });
    }

    render() {
        return (
            <section className="full-height">
                <MainToolbarContents>
                    <ToolbarButton
                        icon="pageview"
                        text="View"
                        tooltip="View the content of this message"
                        disabled={!this.state.viewing}
                        onClick={this.view.bind(this)}
                    />
                </MainToolbarContents>

                <Tabs onTabChange={this.tabChanged.bind(this)}>
                    <TabContainer header="Email Logs">
                        {this.props.emails?.length > 0 ? (
                            <Grid<MessageLog>
                                value={this.props.emails}
                                window={true}
                                collapsed={true}
                                fixedHeader={true}
                                keyField="id"
                                selectionChanged={selection =>
                                    this.setState({ viewing: selection })
                                }
                                cellDoubleClicked={this.view.bind(this)}
                                pageSpecification={this.props.emailsPageSpec}
                                onPageChange={
                                    this.props.setEmailLogPageAndSize
                                }>
                                <GridColumn
                                    header="Sender"
                                    field="sender"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Subject"
                                    field="subject"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Body"
                                    field="body"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Attachments"
                                    field="attachmentCount"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Recipients"
                                    field="recipientCount"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Created"
                                    field="created"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Completed"
                                    field="completed"
                                    selectable={true}
                                />
                            </Grid>
                        ) : (
                            <Text clearanceSide={Side.All}>
                                No emails found.
                            </Text>
                        )}
                    </TabContainer>
                    <TabContainer header="Message Logs">
                        {this.props.messages?.length > 0 ? (
                            <Grid<MessageLog>
                                value={this.props.messages}
                                window={true}
                                collapsed={true}
                                fixedHeader={true}
                                keyField="id"
                                selectionChanged={selection =>
                                    this.setState({ viewing: selection })
                                }
                                cellDoubleClicked={this.view.bind(this)}
                                pageSpecification={this.props.messagesPageSpec}
                                onPageChange={
                                    this.props.setMessageLogPageAndSize
                                }>
                                <GridColumn
                                    header="Sender"
                                    field="sender"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Subject"
                                    field="subject"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Body"
                                    field="body"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Attachments"
                                    field="attachmentCount"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Recipients"
                                    field="recipientCount"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Created"
                                    field="created"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Completed"
                                    field="completed"
                                    selectable={true}
                                />
                            </Grid>
                        ) : (
                            <Text clearanceSide={Side.All}>
                                No messages found.
                            </Text>
                        )}
                    </TabContainer>
                </Tabs>

                <Dialog
                    showDialog={this.state.viewOpen}
                    showLoading={
                        this.props.emailOpenStatus === "loading" ||
                        this.props.messageOpenStatus === "loading"
                    }
                    dialogIcon="search"
                    dialogTitle={this.state.viewTitle}
                    canResize={false}
                    width={850}>
                    <DialogContent>
                        {this.state.tabOpen === "Email Logs" && (
                            <Tabs boxed={true}>
                                <TabContainer header="Details">
                                    <GeneralContainer
                                        bufferSide={Side.All}
                                        bufferSize={Size.Large}>
                                        <h2 className="full">
                                            {this.props.email?.subject}
                                        </h2>

                                        <Panel clearanceSide={Side.Top}>
                                            <i className="inline">
                                                attach_file
                                            </i>
                                            {this.props.email?.attachments.map(
                                                attach => (
                                                    <a
                                                        target="_self"
                                                        className="attachment-item inline"
                                                        href={`./api/Messaging/Attachment?messageId=${encodeURIComponent(
                                                            this.props.email?.id?.toString(),
                                                        )}&title=${encodeURIComponent(
                                                            attach.title,
                                                        )}`}>
                                                        {attach.title}
                                                    </a>
                                                ),
                                            )}
                                        </Panel>

                                        <Panel
                                            clearanceSide={Side.Top}
                                            containContent={true}>
                                            <ContentPortal
                                                content={this.props.email?.body}
                                            />
                                        </Panel>
                                    </GeneralContainer>
                                </TabContainer>

                                <TabContainer header="Recipients">
                                    <Grid<MessageLogRecipient>
                                        height="622px"
                                        paged={true}
                                        window={true}
                                        fullWidth={true}
                                        value={this.props.emailRecipients}
                                        keyField="id"
                                        defaultSortField="address">
                                        <GridColumn
                                            header="Recipient"
                                            field="address"
                                        />
                                        <GridColumn
                                            header="Read"
                                            field="completed"
                                            includeTime={true}
                                        />
                                    </Grid>
                                </TabContainer>
                            </Tabs>
                        )}
                        {this.state.tabOpen === "Message Logs" && (
                            <Tabs boxed={true}>
                                <TabContainer header="Details">
                                    <GeneralContainer
                                        bufferSide={Side.All}
                                        bufferSize={Size.Large}>
                                        <h2 className="full">
                                            {this.props.message?.subject}
                                        </h2>

                                        <Panel clearanceSide={Side.Top}>
                                            <h3>
                                                {this.props.message?.sender}
                                            </h3>
                                        </Panel>

                                        <Panel
                                            clearanceSide={Side.Top}
                                            containContent={true}>
                                            <ContentPortal
                                                content={
                                                    this.props.message?.body
                                                }
                                            />
                                        </Panel>
                                    </GeneralContainer>
                                </TabContainer>

                                <TabContainer header="Recipients">
                                    <Grid<MessageLogRecipient>
                                        height="622px"
                                        paged={true}
                                        window={true}
                                        fullWidth={true}
                                        value={this.props.messageRecipients}
                                        keyField="id"
                                        defaultSortField="address">
                                        <GridColumn
                                            header="Recipient"
                                            field="address"
                                        />
                                        <GridColumn
                                            header="Sent"
                                            field="completed"
                                            includeTime={true}
                                        />
                                    </Grid>
                                </TabContainer>
                            </Tabs>
                        )}
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Close"
                            onClick={() => this.setState({ viewOpen: false })}
                        />
                    </DialogFooter>
                </Dialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { emailLog, messageLog } = state.db;
    return {
        emails: emailLog.pageData,
        emailsPageSpec: emailLog.virtualSpec,
        email: emailLog.openEmail,
        emailRecipients: emailLog.openRecipients,
        emailOpenStatus: emailLog.openStatus,
        messages: messageLog.pageData,
        messagesPageSpec: messageLog.virtualSpec,
        message: messageLog.openMessage,
        messageRecipients: messageLog.openRecipients,
        messageOpenStatus: messageLog.openStatus,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        getEmailLogPage: () => unwrappedDispatch(getEmailLogPage()),
        getMessageLogPage: () => unwrappedDispatch(getMessageLogPage()),
        setEmailLogPageAndSize: (spec: PageSpecification) =>
            unwrappedDispatch(setEmailLogPageAndSize(spec)),
        setMessageLogPageAndSize: (spec: PageSpecification) =>
            unwrappedDispatch(setMessageLogPageAndSize(spec)),
        openEmailLog: (email: MessageLog) =>
            unwrappedDispatch(openEmailLog(email)),
        openMessageLog: (message: MessageLog) =>
            unwrappedDispatch(openMessageLog(message)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteMessageLogs);
